import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Field } from 'formik';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import get from 'lodash.get';
import {
  Grid,
  Typography,
  Card,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  LinearProgress,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import useStyles from '../styles';
import { RESULT_OPTIONS, FINDING_TYPE_SELECT_OPTIONS } from '../constants';
import {
  createChecklistState,
  createFindingEntry,
  buildSummaryText,
  buildDefaultAuditName,
  getTodayDateInputValue,
  countFindingProgress,
  toDateInputValue,
  isTextFilled,
  normalizeFindingType,
} from '../helpers';
import FormInput from '../../../components/FormInput';
import FormSelect from '../../../components/FormSelect';
import FormDatePicker from '../../../components/FormDataPicker';
import PromptNotification from '../../../components/PromptComponent';
import LoadingButton from '../../../components/LoadingButton';
import ModalConfirmation from '../../../components/ModalConfirmation';
import { useCompanyInfo } from '../../../../utils/effects/useCompanyInfo';
import { useUserPermissions } from '../../../../utils/effects/userPermissions';
import { canEditPsmAsUser, isPsmAuditAdministrator, normalizePersonId } from '../../../../utils/psmAuditPermissions';
import { psmAuditDigital, currentCompany } from '../../../../actionTypes';

const validateAuditFindings = values => {
  const errors = {};
  (values.findings || []).forEach((finding, index) => {
    const issued = finding.issuedDate;
    const target = finding.targetDate;
    if (issued && target && target < issued) {
      if (!errors.findings) errors.findings = [];
      const rowErrors = errors.findings[index] || {};
      errors.findings[index] = { ...rowErrors, targetDate: 'Target Date cannot be earlier than Issued Date' };
    }
  });
  return errors;
};

const CreateEditForm = ({ id, auditId, isNew, onBack }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { companyId, companyName } = useCompanyInfo(id);
  const resolvedCompanyId = Number(id || companyId || 0);
  const company = useSelector(state => get(state, `entity.company[${resolvedCompanyId}]`, {}));
  const currentUserId = useSelector(state => state.auth.check.userId);
  const currentUser = useSelector(state => get(state, `entity.user[${currentUserId}]`, {}));
  const checklistSections = useSelector(state => state.psmAuditDigital.questions);
  const currentAudit = useSelector(state => state.psmAuditDigital.currentAudit);
  const createdAuditId = get(currentAudit, 'id', null);
  const auditorName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || '';
  const contractorLabel = company.companyName || companyName || get(company, 'name', '') || '';
  const [expandedSections, setExpandedSections] = useState({});
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const createdAuditIdRef = useRef(null);
  const pendingFormResetRef = useRef(null);
  const saveState = useSelector(state => state.psmAuditDigital);
  const { type: userType } = useUserPermissions();

  useEffect(() => {
    if (!saveState.saveSuccess || !pendingFormResetRef.current) return;
    const { values, resetForm } = pendingFormResetRef.current;
    if (isNew && createdAuditId) {
      createdAuditIdRef.current = createdAuditId;
    }
    resetForm({ values });
    pendingFormResetRef.current = null;
  }, [saveState.saveSuccess, createdAuditId, isNew]);

  useEffect(() => {
    if (saveState.saveError) {
      pendingFormResetRef.current = null;
    }
  }, [saveState.saveError]);

  useEffect(() => {
    dispatch({ type: psmAuditDigital.questions.start });
  }, [dispatch]);

  useEffect(() => {
    if (id && !company.companyName && !company.name) {
      dispatch({ type: currentCompany.info.get.start, payload: { id: resolvedCompanyId } });
    }
  }, [company.companyName, company.name, dispatch, id, resolvedCompanyId]);

  useEffect(() => {
    if (isNew) {
      createdAuditIdRef.current = null;
      dispatch({ type: psmAuditDigital.currentAuditClear });
      return;
    }
    createdAuditIdRef.current = Number(auditId);
    if (auditId) {
      dispatch({ type: psmAuditDigital.getById.start, payload: { auditId: Number(auditId) } });
    }
  }, [auditId, dispatch, isNew]);

  const auditData = isNew ? null : currentAudit;
  const isCompleted = !isNew && get(auditData, 'status') === 'Completed';
  const auditAuditorId = isNew ? currentUserId : normalizePersonId(get(auditData, 'auditor'));
  const canEdit = isNew
    ? isPsmAuditAdministrator(userType)
    : auditData?.editable != null
    ? auditData.editable
    : canEditPsmAsUser(auditAuditorId, currentUserId, userType);
  const isPermissionReadOnly = !isNew && !saveState.currentAuditLoading && Boolean(auditData) && !canEdit;
  const isReadOnly = isCompleted || isPermissionReadOnly;

  useEffect(() => {
    if (!checklistSections.length) {
      return;
    }
    setExpandedSections(
      checklistSections.reduce((acc, section, index) => ({ ...acc, [String(section.id)]: index === 0 }), {}),
    );
  }, [checklistSections]);

  const populatedChecklist = useMemo(() => {
    const state = createChecklistState(checklistSections);
    const answers = get(auditData, 'answers', []);
    answers.forEach(item => {
      const questionKey = String(item.questionId);
      Object.keys(state).forEach(sectionKey => {
        if (state[sectionKey] && state[sectionKey][questionKey]) {
          state[sectionKey][questionKey] = {
            answer: item.answer === 'NA' ? 'N/A' : item.answer || '',
            comment: item.comment || '',
          };
        }
      });
    });
    return state;
  }, [auditData, checklistSections]);

  const populatedFindings = useMemo(() => {
    const findings = get(auditData, 'findings', []);
    if (!findings.length) {
      return auditData ? [] : [createFindingEntry()];
    }
    return findings.map(item => ({
      id: `existing-${item.id}`,
      findingId: item.id,
      type: normalizeFindingType(item.findingType || 'Finding'),
      shortDescription: item.shortDescription || '',
      issuedDate: toDateInputValue(item.issuedDate),
      targetDate: toDateInputValue(item.targetDate),
      comment: item.comment || '',
      detailedDescription: item.detailedDescription || '',
    }));
  }, [auditData]);

  const initialValues = useMemo(
    () => ({
      contractorId: get(auditData, 'contractor.id', resolvedCompanyId),
      contractor: get(auditData, 'contractor.name', contractorLabel),
      auditor: get(auditData, 'auditor.name', auditorName),
      auditName: isNew
        ? buildDefaultAuditName(contractorLabel)
        : get(auditData, 'name', '') || buildDefaultAuditName(get(auditData, 'contractor.name', contractorLabel)),
      auditConductedWeek: isNew ? getTodayDateInputValue() : toDateInputValue(auditData && auditData.date),
      checklist: populatedChecklist,
      findings: populatedFindings,
      submitMode: 'draft',
      observation: get(auditData, 'observation', '') || '',
      recommendation: get(auditData, 'recommendation', '') || '',
      summary:
        get(auditData, 'summary', '') ||
        buildSummaryText({
          auditConductedWeek: isNew ? getTodayDateInputValue() : '',
          findingsCount: isNew ? 1 : 0,
          recommendationsCount: 0,
          bestPracticesCount: 0,
          commentsCount: 0,
        }),
    }),
    [auditData, auditorName, contractorLabel, populatedChecklist, populatedFindings, resolvedCompanyId, isNew],
  );

  const title = isNew ? 'New PSM Audit' : 'Edit PSM Audit';

  return (
    <Formik
      initialValues={initialValues}
      validate={validateAuditFindings}
      onSubmit={(values, { resetForm }) => {
        if (isCompleted || (!isNew && !canEdit)) {
          return;
        }

        const answers = checklistSections
          .reduce(
            (acc, section) => {
              const sectionAnswers = (section.questions || []).map(question => {
                const sectionKey = String(section.id);
                const questionKey = String(question.id);
                const checklistEntry = get(values, `checklist.${sectionKey}.${questionKey}`, {
                  answer: '',
                  comment: '',
                });
                return {
                  id: Number(question.id),
                  questionId: Number(question.id),
                  answer: checklistEntry.answer === 'N/A' ? 'NA' : checklistEntry.answer || '',
                  comment: checklistEntry.comment || '',
                };
              });

              return {
                items: [...acc.items, ...sectionAnswers],
              };
            },
            { items: [] },
          )
          .items.filter(item => isTextFilled(item.answer));

        const persistedAuditId = isNew ? createdAuditIdRef.current : Number(auditId);
        const payload = {
          ...(persistedAuditId ? { id: Number(persistedAuditId) } : {}),
          contractorId: Number(values.contractorId || resolvedCompanyId || 0),
          name: values.auditName,
          date: values.auditConductedWeek,
          answers,
          findings: values.findings.map((item, index) => ({
            id: item.findingId || index,
            findingType: item.type,
            issuedDate: item.issuedDate,
            targetDate: item.targetDate,
            shortDescription: item.shortDescription,
            comment: item.comment,
            detailedDescription: item.detailedDescription,
          })),
          summary: values.summary,
          observation: values.observation || '',
          recommendation: values.recommendation || '',
          isDraft: values.submitMode !== 'complete',
        };
        pendingFormResetRef.current = { values, resetForm };
        if (isNew && !persistedAuditId) {
          dispatch({ type: psmAuditDigital.create.start, payload: { companyId: resolvedCompanyId, data: payload } });
          return;
        }
        dispatch({ type: psmAuditDigital.update.start, payload: { companyId: resolvedCompanyId, data: payload } });
      }}
      enableReinitialize
    >
      {props => {
        const findings = props.values.findings;
        const findingsCount = findings.filter(item => item.type === 'Finding').length;
        const recommendationsCount = findings.filter(item => item.type === 'Recommendation').length;
        const bestPracticesCount = findings.filter(item => item.type === 'BestPractice').length;
        const commentsCount = findings.filter(item => item.type === 'Comment').length;
        const totalChecklistItems = checklistSections.reduce(
          (acc, section) => acc + (section.questions || []).length,
          0,
        );
        const answeredItems = checklistSections.reduce((sectionAcc, section) => {
          const sectionKey = String(section.id);
          return (
            sectionAcc +
            (section.questions || []).filter(question => {
              const answer = get(props.values, `checklist.${sectionKey}.${String(question.id)}.answer`, '');
              return answer && answer !== '';
            }).length
          );
        }, 0);
        const { completed: completedFindingFields, total: totalFindingFields } = countFindingProgress(findings);
        const auditDateComplete = isTextFilled(props.values.auditConductedWeek);
        const totalProgressUnits = totalChecklistItems + totalFindingFields + 1;
        const completedProgressUnits = answeredItems + completedFindingFields + (auditDateComplete ? 1 : 0);
        const progressPercent = totalProgressUnits
          ? Math.round((completedProgressUnits / totalProgressUnits) * 100)
          : 0;
        const canCompleteAudit = progressPercent === 100;

        const regenerateSummary = () => {
          props.setFieldValue(
            'summary',
            buildSummaryText({
              auditConductedWeek: props.values.auditConductedWeek,
              findingsCount,
              recommendationsCount,
              bestPracticesCount,
              commentsCount,
            }),
          );
        };

        const addFinding = () => props.setFieldValue('findings', [...findings, createFindingEntry()]);
        const removeFinding = entryId =>
          props.setFieldValue(
            'findings',
            findings.filter(item => item.id !== entryId),
          );

        return (
          <>
            <ModalConfirmation
              isModalOpen={completeConfirmOpen}
              title="Complete Audit?"
              confirmButtonText="Confirm"
              cancelButtonText="Cancel"
              onCancel={() => setCompleteConfirmOpen(false)}
              onConfirm={() => {
                setCompleteConfirmOpen(false);
                props.setFieldValue('submitMode', 'complete', false);
                props.handleSubmit();
              }}
            >
              <Typography variant="body2">
                Once completed, the audit will become read-only and no further changes can be made.
              </Typography>
            </ModalConfirmation>
            <PromptNotification formStatus={!isReadOnly && props.dirty && !saveState.pendingCompanyRedirect} />
            <Grid container className={classes.root} spacing={3}>
              <Grid item xs={12}>
                <Button startIcon={<ArrowBackIcon />} onClick={onBack} className={classes.backButton}>
                  Back to Audits
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <Typography variant="h5" className={classes.pageTitle}>
                    {title}
                  </Typography>
                  {!isNew && (
                    <Typography variant="body2" color="textSecondary">
                      Company ID: {id} · Audit ID: {auditId}
                    </Typography>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Field component={FormInput} label="Audit Name" name="auditName" disabled={isReadOnly} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Field component={FormInput} label="Contractor" name="contractor" disabled />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Field component={FormInput} label="Auditor" name="auditor" disabled />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Field
                        component={FormDatePicker}
                        label="Audit Conducted Date"
                        name="auditConductedWeek"
                        disabled={isReadOnly}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Audit Checklist
                  </Typography>
                  {saveState.questionsLoading && <LinearProgress className={classes.progressBar} />}
                  {checklistSections.map(section => {
                    const sectionKey = String(section.id);
                    return (
                      <Card key={sectionKey} variant="outlined" className={classes.checklistSectionCard}>
                        <div
                          role="button"
                          tabIndex={0}
                          aria-expanded={Boolean(expandedSections[sectionKey])}
                          className={classes.checklistSectionHeader}
                          onClick={() =>
                            setExpandedSections(prev => ({
                              ...prev,
                              [sectionKey]: !prev[sectionKey],
                            }))
                          }
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setExpandedSections(prev => ({
                                ...prev,
                                [sectionKey]: !prev[sectionKey],
                              }));
                            }
                          }}
                        >
                          <Typography variant="subtitle1" className={classes.checklistSectionTitle}>
                            {section.title}
                          </Typography>
                          <ExpandMoreIcon
                            fontSize="small"
                            className={`${classes.expandIcon} ${
                              expandedSections[sectionKey] ? classes.expandIconOpen : ''
                            }`}
                            aria-hidden
                          />
                        </div>
                        {expandedSections[sectionKey] && (
                          <div className={classes.checklistSectionBody}>
                            {(section.questions || []).map(question => {
                              const questionKey = String(question.id);
                              const fieldPath = `checklist.${sectionKey}.${questionKey}`;
                              const checklistEntry = get(props.values, fieldPath, { answer: '', comment: '' });
                              return (
                                <Card key={questionKey} variant="outlined" className={classes.checklistItemCard}>
                                  <Typography variant="body2" className={classes.checklistItemLabel}>
                                    {question.title}
                                  </Typography>
                                  <RadioGroup
                                    row
                                    value={checklistEntry.answer}
                                    onChange={
                                      isReadOnly
                                        ? undefined
                                        : event =>
                                            props.setFieldValue(fieldPath, {
                                              ...checklistEntry,
                                              answer: event.target.value,
                                            })
                                    }
                                  >
                                    {RESULT_OPTIONS.map(option => (
                                      <FormControlLabel
                                        key={option}
                                        value={option}
                                        control={<Radio color="primary" size="small" disabled={isReadOnly} />}
                                        label={option}
                                        disabled={isReadOnly}
                                      />
                                    ))}
                                  </RadioGroup>
                                  <Field
                                    component={FormInput}
                                    multiline
                                    rows={2}
                                    placeholder="Add comment (optional)..."
                                    name={`${fieldPath}.comment`}
                                    disabled={isReadOnly}
                                  />
                                </Card>
                              );
                            })}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <div className={classes.findingsHeader}>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      Findings & Recommendations
                    </Typography>
                    {!isReadOnly && (
                      <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={addFinding}>
                        Add Finding / Recommendation
                      </Button>
                    )}
                  </div>

                  {findings.map((entry, index) => (
                    <Card key={entry.id} variant="outlined" className={classes.findingCard}>
                      <div className={classes.findingCardHeader}>
                        <Typography variant="subtitle1">Finding / Recommendation #{index + 1}</Typography>
                        {!isReadOnly && (
                          <Button
                            size="small"
                            color="secondary"
                            startIcon={<DeleteIcon />}
                            onClick={() => removeFinding(entry.id)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                      <Divider className={classes.findingDivider} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Field
                            component={FormSelect}
                            label="Type"
                            name={`findings.${index}.type`}
                            options={FINDING_TYPE_SELECT_OPTIONS}
                            disabled={isReadOnly}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            component={FormDatePicker}
                            label="Issued Date"
                            name={`findings.${index}.issuedDate`}
                            disabled={isReadOnly}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            component={FormDatePicker}
                            label="Target Date"
                            name={`findings.${index}.targetDate`}
                            disabled={isReadOnly}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            component={FormInput}
                            label="Description"
                            placeholder="Comment"
                            name={`findings.${index}.shortDescription`}
                            disabled={isReadOnly}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            component={FormInput}
                            label="Comment (optional)"
                            name={`findings.${index}.comment`}
                            disabled={isReadOnly}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  ))}

                  <Card variant="outlined" className={classes.totalsCard}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="overline" className={classes.totalLabel}>
                          Total Findings
                        </Typography>
                        <Typography variant="h5" className={classes.totalFindingValue}>
                          {findingsCount}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="overline" className={classes.totalLabel}>
                          Total Recommendations
                        </Typography>
                        <Typography variant="h5" className={classes.totalRecommendationValue}>
                          {recommendationsCount}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="overline" className={classes.totalLabel}>
                          Total Best Practices
                        </Typography>
                        <Typography variant="h5" className={classes.totalBestPracticeValue}>
                          {bestPracticesCount}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="overline" className={classes.totalLabel}>
                          Total Comments
                        </Typography>
                        <Typography variant="h5" className={classes.totalCommentValue}>
                          {commentsCount}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Card>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Detailed Findings
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className={classes.detailedFindingsIntro}>
                    The following consolidated narrative summarises all findings and recommendations identified during
                    this audit.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Field
                        component={FormInput}
                        label="Observation (optional)"
                        name="observation"
                        multiline
                        rows={4}
                        placeholder="Describe the overall observation narrative..."
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        component={FormInput}
                        label="Recommendation (optional)"
                        name="recommendation"
                        multiline
                        rows={4}
                        placeholder="Describe the overall recommendation narrative..."
                        disabled={isReadOnly}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card className={classes.sectionCard}>
                  <div className={classes.summaryHeader}>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      Summary
                    </Typography>
                    {!isReadOnly && (
                      <Button variant="outlined" color="primary" onClick={regenerateSummary}>
                        Regenerate Summary
                      </Button>
                    )}
                  </div>
                  <Field component={FormInput} multiline rows={5} name="summary" disabled={isReadOnly} />
                </Card>
              </Grid>

              <Grid item xs={12} className={classes.stickyFooterCard}>
                <Card className={classes.footerCard}>
                  <div className={classes.progressRow}>
                    <Typography variant="body2" className={classes.progressLabel}>
                      Progress: {progressPercent}% Complete
                    </Typography>
                    <LinearProgress variant="determinate" value={progressPercent} className={classes.progressBar} />
                  </div>
                  <div className={classes.footerActions}>
                    <Button onClick={onBack}>{isReadOnly ? 'Back' : 'Cancel'}</Button>
                    {!isReadOnly && (
                      <>
                        <Button
                          variant="outlined"
                          color="primary"
                          disabled={saveState.saving}
                          onClick={() => {
                            props.setFieldValue('submitMode', 'draft', false);
                            props.submitForm();
                          }}
                        >
                          Save Progress
                        </Button>
                        <div className={classes.loadingButtonInline}>
                          <LoadingButton
                            skipCheckPQF
                            disabled={!canCompleteAudit}
                            loading={saveState.saving}
                            success={saveState.saveSuccess}
                            error={saveState.saveError}
                            onClick={() => setCompleteConfirmOpen(true)}
                            text="Complete Audit"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              </Grid>
            </Grid>
          </>
        );
      }}
    </Formik>
  );
};

export default CreateEditForm;
